/**
 * Operate queue — V98-5-1 (ProgramShell Operate tab).
 *
 * One list per program by source type (checks · recurring tasks · findings ·
 * acceptances · change notices) with a single-select filter, each row's
 * deadline + "how the deadline was set", cadence staleness, the path the item
 * takes to close, and the actions this viewer may take.
 *
 * V98-5 review:
 *   T5-B5   no second "open" number: the program band above is the count;
 *           the filters count the rows listed ("All 10 · Checks 3 …").
 *   T5-B6   chips say "FedRAMP rule" / "Your rule" (the id in a tooltip);
 *           "How the deadline was set" is two human lines, the id / version
 *           / raw basis behind "Show rule".
 *   T5-H7   status chips are borderless; "Closes automatically" is a lock
 *           glyph + muted text.
 *   T5-H13  each path step names who; "nobody eligible" only when routing
 *           found nobody.
 *   T5-H14  a finding opens /findings?finding=<id>.
 *   ?finding=<id> (the item page's "Fix in Operate") scrolls to and focuses
 *           that finding's row, switching to "Closed this week" if it closed.
 *   T5-H15  after a write, focus moves to the next row (or the heading).
 *   Medium  one outline action per row + "⋯" (Reassign, Deadline, Close);
 *           Close… needs "How it was fixed"; Deadline… is hidden once a
 *           rule-set date has passed; the toast follows the close rule.
 *
 * Reads: GET /programs/:program/operate (any member). Writes: /findings/:id/*
 * (remediate: owner or owner/admin/editor; reassign / deadline: owner/admin
 * or the program lead; close: owner/admin/editor — the server decides).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Field, Input, Select, Spinner, Textarea } from '@aigovops/design-system';
import type { OperateItem, OperateSource } from '@aigovops/core';
import { closeFinding, editFindingDeadline, getOperateQueue, reassignFinding, remediateFinding } from '../../lib/cert-ops-api';
import { fmtDate, howDeadlineWasSet } from '../../lib/cert-ops-model';
import { PROGRAM_LABELS } from '../../lib/nav-config';
import { firstName, nameOf, type Names } from '../../lib/today-model';
import { ActionDialog, Chip, errorText, useAnnounce, useFocusAfter } from '../today/shared';
import { decideRemediationDraft, getRemediationSummary, listFindings, listRemediationDrafts } from '../../lib/api';
import { queryKeys } from '../../lib/query-keys';
import { pickDrafts, RemediationDraft, sameAsPlan } from '../RemediationDraft';
import '../../pages/cert-ops.css';

type Filter = 'all' | OperateSource | 'closed';
type DialogKind = 'remediate' | 'reassign' | 'deadline' | 'close';
const SOURCE_LABEL: Record<OperateSource, string> = {
  check: 'Checks', recurring_task: 'Recurring tasks', finding: 'Findings', accepted_weakness: 'Acceptances', change_notice: 'Change notices',
};
const STEP_LABEL: Record<string, string> = {
  review: 'Review', remediate: 'Remediate', reconnect: 'Reconnect', classify: 'Classify', co_approve: 'Co-approve', send: 'Send',
  complete: 'Do it', approve: 'Approve', renew: 'Renew', verify: 'Verify', close: 'Close',
};
const DAY = 86_400_000;
const rowId = (key: string) => `op-${key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;

export function LockGlyph() {
  return <svg aria-hidden="true" width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="7" width="10" height="7" rx="1.5" /><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" /></svg>;
}

export function OperateQueue({ slug, prefix, program, me, role, canManage, names }: {
  slug: string; prefix: string; program: string; me: string; role: string; canManage: boolean; names: Names;
}) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['operate', slug, program], queryFn: () => getOperateQueue(slug, program), retry: false, staleTime: 30_000 });
  const [filter, setFilter] = useState<Filter>('all');
  const [dialog, setDialog] = useState<{ kind: DialogKind; item: OperateItem } | null>(null);
  const [announceNode, announce] = useAnnounce();
  const focusLater = useFocusAfter();
  const queue = q.data?.queue;
  const rows = useMemo(() => {
    if (!queue) return [];
    if (filter === 'closed') return queue.closed;
    return filter === 'all' ? queue.items : queue.items.filter((i) => i.source === filter);
  }, [queue, filter]);
  // Deep link from the item page: ?finding=<id> → that finding's row.
  const [sp] = useSearchParams();
  const focusFinding = sp.get('finding');
  useEffect(() => {
    if (!focusFinding || !queue) return;
    const match = (i: OperateItem) => i.ref.kind === 'finding' && i.ref.id === focusFinding;
    const hit = queue.items.find(match) ?? queue.closed.find(match);
    if (!hit) return;
    if (!queue.items.includes(hit)) setFilter('closed');
    else setFilter((f) => (f === 'all' || f === hit.source ? f : 'all'));
    requestAnimationFrame(() => {
      const el = document.getElementById(rowId(hit.key));
      el?.scrollIntoView?.({ block: 'center' });
      el?.focus();
    });
  }, [focusFinding, queue]);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['operate', slug, program] }), qc.invalidateQueries({ queryKey: ['today', slug] }), qc.invalidateQueries({ queryKey: ['programs', slug] })]);
  const editorish = role === 'owner' || role === 'admin' || role === 'editor';
  // V101-5-4: one request for every finding's coach state → a "Plan" chip on rows with an accepted plan.
  const coach = useQuery({ queryKey: queryKeys.remediationSummary(slug), queryFn: () => getRemediationSummary(slug), retry: false, staleTime: 30_000 });

  if (q.isLoading) return <Spinner label="Loading the operate queue" />;
  if (q.error || !queue) return <Alert variant="error"><p>The operate queue could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>;
  const c = queue.counts;
  const today = Date.now();
  const after = (key: string) => {
    const i = rows.findIndex((r) => r.key === key);
    const next = rows[i + 1] ?? rows[i - 1];
    focusLater(next && next.key !== key ? rowId(next.key) : 'op-h');
  };

  return (
    <section className="gp-sec" aria-labelledby="op-h" data-part="operate-queue">
      {announceNode}
      <h3 id="op-h" className="gp-sec-h" tabIndex={-1}>Needs action</h3>
      <ul className="gp-filter" aria-label="Show">
        <li><button type="button" aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>{`All ${queue.items.length}`}</button></li>
        {(Object.keys(SOURCE_LABEL) as OperateSource[]).filter((s) => c.by_source[s] > 0).map((s) => (
          <li key={s}><button type="button" aria-pressed={filter === s} onClick={() => setFilter(s)}>{`${SOURCE_LABEL[s]} ${c.by_source[s]}`}</button></li>
        ))}
        {c.closed_7d ? <li><button type="button" aria-pressed={filter === 'closed'} onClick={() => setFilter('closed')}>{`Closed this week ${c.closed_7d}`}</button></li> : null}
      </ul>
      <p role="status" className="gp-sr-only">{`${rows.length} shown`}</p>
      {rows.length === 0 ? (
        <p className="gp-dim">{filter === 'closed' ? 'Nothing closed this week.' : 'Nothing needs action in this program.'}</p>
      ) : (
        <ul className="gp-owed-list gp-op-list" aria-labelledby="op-h">
          {rows.map((i) => {
            const how = howDeadlineWasSet(i.deadline);
            const owner = i.owner_user_id ? nameOf(names, i.owner_user_id) : 'Unassigned';
            const date = i.deadline?.date ?? null;
            const late = date ? Math.floor((today - Date.parse(date)) / DAY) : 0;
            const due = !date ? null : i.overdue ? { text: `${Math.max(1, late)} day${late === 1 ? '' : 's'} overdue`, tone: 'red' as const }
              : { text: `Due ${fmtDate(date)}`, tone: Date.parse(date) - today <= 14 * DAY ? 'amber' as const : 'neutral' as const };
            const isFinding = i.ref.kind === 'finding';
            const mine = i.owner_user_id === me;
            const open = i.ref.kind === 'acceptance' ? `${prefix}/exceptions?acceptance=${encodeURIComponent(i.ref.id)}`
              : i.ref.kind === 'connector' ? `${prefix}/settings/integrations`
                : i.ref.kind === 'finding' ? `${prefix}/findings?finding=${encodeURIComponent(i.ref.id)}`
                  : `${prefix}/items/${i.ref.kind}/${encodeURIComponent(i.ref.id)}?from=program:${program}`;
            const canRemediate = isFinding && i.actions.includes('remediate') && (mine || editorish);
            const lockedPast = !!i.deadline?.rule_locked && !!date && Date.parse(date) < today;
            const more: Array<{ kind: DialogKind; label: string }> = [
              ...(isFinding && i.actions.includes('reassign') && canManage ? [{ kind: 'reassign' as const, label: 'Reassign…' }] : []),
              ...(isFinding && i.actions.includes('deadline') && canManage && !lockedPast ? [{ kind: 'deadline' as const, label: 'Deadline…' }] : []),
              ...(isFinding && i.actions.includes('close') && editorish ? [{ kind: 'close' as const, label: 'Close…' }] : []),
            ];
            return (
              <li key={i.key} className="gp-ow gp-op-row" data-operate={i.key} id={rowId(i.key)} tabIndex={-1}>
                <span className="gp-ow-main">
                  <span className="gp-ow-line"><Link className="gp-ow-t" to={open}>{i.title}</Link>{i.context ? <span className="gp-dim">{i.context}</span> : null}</span>
                  <span className="gp-ow-meta">
                    {due ? <Chip text={due.text} tone={due.tone} /> : null}
                    {isFinding && coach.data?.summary[i.ref.id]?.plan_accepted ? <Chip text="Plan" tone="ok" /> : null}
                    <span className="gp-chip gp-chip--neutral" title={how.title ?? undefined}>{how.label}</span>
                    <span>{owner}</span>
                    {i.staleness?.stale ? <span>{i.staleness.missed > 1 ? `${i.staleness.missed} runs missed · ${i.staleness.cadence}` : `${i.staleness.cadence.charAt(0).toUpperCase()}${i.staleness.cadence.slice(1)} · ${i.staleness.last_at ? `last done ${fmtDate(i.staleness.last_at)}` : 'never done'}`}</span> : null}
                    {i.also_in.length ? <span>{`Also in ${i.also_in.map((k) => PROGRAM_LABELS[k as keyof typeof PROGRAM_LABELS] ?? k).join(', ')}`}</span> : null}
                    {!i.close_rule.manual_close && i.close_rule.mode === 'automated' ? <span className="gp-auto"><LockGlyph />Closes automatically</span> : null}
                  </span>
                  <ol className="gp-path" aria-label="Path to close">
                    {i.path.map((p, n) => (
                      <li key={`${p.step}-${n}`} aria-current={p.current ? 'step' : undefined}>
                        {`${STEP_LABEL[p.step] ?? p.step} · ${p.who === 'auto' ? 'auto' : p.who ? (p.who === me ? 'you' : firstName(nameOf(names, p.who))) : p.routing === 'none_eligible' ? 'nobody eligible' : 'unassigned'}`}
                      </li>
                    ))}
                  </ol>
                  <details className="gp-how">
                    <summary>How the deadline was set<span className="gp-sr-only">{` · ${i.title}`}</span></summary>
                    <ul className="gp-plain-list">
                      {how.lines.map((l) => <li key={l}>{l}</li>)}
                      <li>{i.close_rule.text}</li>
                    </ul>
                    {how.rule.length ? (
                      <details className="gp-how gp-how--nested"><summary>Show rule<span className="gp-sr-only">{` · ${i.title}`}</span></summary>
                        <ul className="gp-plain-list">{how.rule.map((l) => <li key={l}>{l}</li>)}</ul>
                      </details>
                    ) : null}
                  </details>
                </span>
                <span className="gp-ow-act gp-op-act">
                  {canRemediate
                    ? <Button compact onClick={() => setDialog({ kind: 'remediate', item: i })} aria-label={`Remediate · ${i.title}`}>Remediate…</Button>
                    : <Link className="gp-linkbtn gp-linkbtn--compact" to={open} aria-label={`Open · ${i.title}`}>Open</Link>}
                  {more.length ? <MoreMenu title={i.title} items={more} onPick={(kind) => setDialog({ kind, item: i })} /> : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <FindingDialog slug={slug} prefix={prefix} role={role} names={names} me={me} dialog={dialog} onClose={() => setDialog(null)}
        onDone={async (m, key) => { setDialog(null); await refresh(); after(key); announce(m); }} />
    </section>
  );
}

/** "⋯" — the row's other actions, as a disclosure of buttons (keyboard: Tab through them; Escape closes). */
function MoreMenu({ title, items, onPick }: { title: string; items: Array<{ kind: DialogKind; label: string }>; onPick: (k: DialogKind) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="gp-more" onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); (e.currentTarget.querySelector('button') as HTMLButtonElement | null)?.focus(); } }}>
      <Button compact variant="ghost" aria-expanded={open} aria-label={`More actions · ${title}`} onClick={() => setOpen((x) => !x)}>⋯</Button>
      {open ? (
        <span className="gp-more-list">
          {items.map((x) => <Button key={x.kind} compact variant="ghost" aria-label={`${x.label.replace('…', '')} · ${title}`} onClick={() => { setOpen(false); onPick(x.kind); }}>{x.label}</Button>)}
        </span>
      ) : null}
    </span>
  );
}

function FindingDialog({ slug, prefix, role, names, me, dialog, onClose, onDone }: {
  slug: string; prefix: string; role: string; names: Names; me: string; dialog: { kind: DialogKind; item: OperateItem } | null;
  onClose: () => void; onDone: (m: string, key: string) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [who, setWho] = useState('');
  const [date, setDate] = useState('');
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Nit: the secondary ("I fixed it") has its own busy flag.
  const [busy2, setBusy2] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [planKept, setPlanKept] = useState('');
  const item = dialog?.item;
  const kind = dialog?.kind;
  // V101-5-4: the coach needs the finding's kind, which the operate row doesn't carry — the (shared, member-readable) findings list does.
  const findingId = item?.ref.kind === 'finding' ? item.ref.id : null;
  const findings = useQuery({ queryKey: queryKeys.findings(slug), queryFn: () => listFindings(slug), enabled: kind === 'remediate' && !!findingId, retry: false, staleTime: 60_000 });
  const finding = findingId ? findings.data?.findings.find((f) => f.finding_id === findingId) : undefined;
  const qc = useQueryClient();
  const canWrite = role === 'owner' || role === 'admin' || role === 'editor';
  // H1: an open control-check finding puts the Fix first; a ready draft makes "Use as plan" the dialog's submit.
  const coach = kind === 'remediate' && finding?.kind === 'control_check_failure' && (finding.status === 'open' || finding.status === 'acknowledged');
  const drafts = useQuery({ queryKey: queryKeys.remediationDrafts(slug, findingId ?? ''), queryFn: () => listRemediationDrafts(slug, findingId!), enabled: coach && !!findingId, retry: false, staleTime: 30_000 });
  const [held, setHeld] = useState<string | null>(null);
  const picked = pickDrafts(drafts.data?.drafts ?? []);
  // M3: a draft held for Undo counts as no draft, so the primary doesn't jump around.
  const live = picked.current && picked.current.draft_id !== held ? picked.current : null;
  // M5: a new draft identical to the plan is not offered as "Replace plan".
  const ready = coach && canWrite && live?.status === 'draft' && !sameAsPlan(live, picked.plan) ? live : null;
  const noDraftYet = coach && canWrite && drafts.isSuccess && !live && !picked.plan;
  // H1: while the coach's action leads, the note waits behind "I fixed it".
  const noteHidden = (!!ready || noDraftYet) && !noteOpen;
  const openNote = () => { setNoteOpen(true); window.setTimeout(() => document.getElementById('op-note')?.focus(), 0); };
  const locked = !!item?.deadline?.rule_locked;
  const current = item?.deadline?.date ?? null;
  const reset = () => { setText(''); setWho(''); setDate(''); setErrs({}); setErr(null); setNoteOpen(false); setPlanKept(''); };
  const title = kind === 'remediate' ? 'Remediate' : kind === 'reassign' ? 'Reassign' : kind === 'close' ? 'Close' : 'Change the deadline';
  const firstBad = (e: Record<string, string>) => (e.who ? 'op-who' : e.date ? 'op-date' : e.text ? (kind === 'deadline' ? 'op-why' : kind === 'reassign' ? 'op-reason' : 'op-note') : null);
  const acceptPlan = async () => {
    if (!item || !ready) return false;
    setBusy(true); setErr(null);
    try {
      await decideRemediationDraft(slug, item.ref.id, ready.draft_id, 'accept');
      await Promise.all([qc.invalidateQueries({ queryKey: queryKeys.remediationDrafts(slug, item.ref.id) }), qc.invalidateQueries({ queryKey: queryKeys.remediationSummary(slug) })]);
      // H1: a note already typed is never lost — the dialog stays open (Cancel → Done) so it can still be sent.
      if (text.trim()) { setPlanKept('Plan attached · closes when the check passes'); return false; }
      await onDone('Plan attached · closes when the check passes', item.key);
      reset();
      return true;
    } catch (x) {
      setErr(errorText(x, 'That did not go through.'));
      await qc.invalidateQueries({ queryKey: queryKeys.remediationDrafts(slug, item.ref.id) });
      return false;
    } finally { setBusy(false); }
  };
  const submit = async () => {
    if (!item || !kind) return false;
    if (ready) return acceptPlan();
    return markFixed();
  };
  const markFixed = async (secondary = false) => {
    if (!item || !kind) return false;
    if (secondary && noteHidden) { openNote(); return false; }
    const e: Record<string, string> = {};
    if ((kind === 'remediate' || kind === 'close') && text.trim().length < 10) e.text = 'Say how it was fixed (at least 10 characters).';
    if (kind === 'reassign' && !who) e.who = 'Choose who takes it.';
    if (kind === 'reassign' && text.trim().length < 3) e.text = 'Give a reason.';
    if (kind === 'deadline') {
      if (text.trim().length < 10) e.text = 'Say why the deadline moves (at least 10 characters).';
      if (!date) e.date = 'Choose the new deadline.';
      else if (locked && current && `${date}T23:59:59.000Z` > current) e.date = `A rule sets this date: it can only move earlier than ${fmtDate(current)}.`;
    }
    setErrs(e);
    const bad = firstBad(e);
    if (bad) { document.getElementById(bad)?.focus(); return false; }
    const setB = secondary ? setBusy2 : setBusy;
    setB(true); setErr(null);
    try {
      if (kind === 'remediate') {
        await remediateFinding(slug, item.ref.id, text.trim());
        await onDone(item.close_rule.manual_close ? 'Marked fixed · close it once it’s verified' : `Marked fixed · ${item.close_rule.text.charAt(0).toLowerCase()}${item.close_rule.text.slice(1)}`, item.key);
      } else if (kind === 'close') {
        // "How it was fixed" is recorded first (remediate), then the close.
        if (item.status !== 'remediated') await remediateFinding(slug, item.ref.id, text.trim());
        await closeFinding(slug, item.ref.id);
        await onDone(`Closed · ${item.title}`, item.key);
      } else if (kind === 'reassign') { await reassignFinding(slug, item.ref.id, { to_user_id: who, reason: text.trim() }); await onDone(`Reassigned to ${nameOf(names, who)}`, item.key); }
      else { await editFindingDeadline(slug, item.ref.id, { due_at: `${date}T23:59:59.000Z`, reason: text.trim() }); await onDone(`Deadline moved to ${fmtDate(date)}`, item.key); }
      reset();
      return true;
    } catch (x) { setErr(errorText(x, 'That did not go through.')); return false; }
    finally { setB(false); }
  };
  const closeLine = kind === 'remediate' && item ? (item.close_rule.manual_close ? 'Close it once it’s verified.' : item.close_rule.text) : null;
  return (
    <ActionDialog open={!!dialog} title={item ? `${title} · ${item.title}` : title}
      submitLabel={ready ? (picked.plan ? 'Replace plan' : 'Use as plan') : kind === 'remediate' ? (coach ? 'I fixed it' : 'Mark fixed') : kind === 'reassign' ? 'Reassign' : kind === 'close' ? 'Close it' : 'Save deadline'}
      hideSubmit={noDraftYet || (!!planKept && !text.trim())}
      secondary={ready || noDraftYet ? { label: 'I fixed it', onClick: () => void markFixed(true), busy: busy2 } : null}
      initialFocus={coach || (kind === 'remediate' && item?.source === 'check') ? 'title' : 'first'}
      subtitle={closeLine}
      cancelLabel={planKept ? 'Done' : 'Cancel'}
      busy={busy} error={err} onClose={() => { reset(); setHeld(null); onClose(); }} onSubmit={submit}>
      {kind === 'remediate' || kind === 'close' ? (
        <>
          {coach && finding ? (
            <RemediationDraft slug={slug} prefix={prefix} role={role} mode="dialog" onHold={setHeld}
              onDescribe={openNote}
              finding={{ finding_id: finding.finding_id, kind: finding.kind!, status: finding.status, title: item?.title, owner_user_id: item?.owner_user_id }} />
          ) : null}
          {kind === 'close' ? <p className="gp-dim">Recorded with your name; it closes now.</p> : null}
          {planKept ? <p role="status" className="gp-dim">{planKept}</p> : null}
          {noteHidden ? null : (
            <Field id="op-note" label="How it was fixed" required error={errs.text}>
              <Textarea rows={3} maxLength={2000} value={text} onChange={(e) => { setText(e.currentTarget.value); setErrs((x) => ({ ...x, text: '' })); }} />
            </Field>
          )}
        </>
      ) : kind === 'reassign' ? (
        <>
          <Field id="op-who" label="New owner" required error={errs.who}>
            <Select value={who} onChange={(e) => { setWho(e.currentTarget.value); setErrs((x) => ({ ...x, who: '' })); }}>
              <option value="">Choose…</option>
              {[...names.entries()].filter(([u]) => u !== item?.owner_user_id).map(([u, n]) => <option key={u} value={u}>{u === me ? `${n} (you)` : n}</option>)}
            </Select>
          </Field>
          <Field id="op-reason" label="Reason" required error={errs.text}>
            <Textarea rows={2} maxLength={2000} value={text} onChange={(e) => { setText(e.currentTarget.value); setErrs((x) => ({ ...x, text: '' })); }} />
          </Field>
        </>
      ) : kind === 'deadline' ? (
        <>
          <p className="gp-dim">{locked ? 'A rule sets this date: it can only move earlier.' : 'Your own rule sets this date: it can move either way, with a reason.'}</p>
          <Field id="op-date" label="New deadline" required hint={current ? `Now ${fmtDate(current)}` : undefined} error={errs.date}>
            <Input type="date" min={new Date().toISOString().slice(0, 10)} max={locked && current ? current.slice(0, 10) : undefined} value={date}
              onChange={(e) => { setDate(e.currentTarget.value); setErrs((x) => ({ ...x, date: '' })); }} />
          </Field>
          <Field id="op-why" label="Why it moves" required error={errs.text}>
            <Textarea rows={2} maxLength={2000} value={text} onChange={(e) => { setText(e.currentTarget.value); setErrs((x) => ({ ...x, text: '' })); }} />
          </Field>
        </>
      ) : null}
    </ActionDialog>
  );
}
